import { Fragment, useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  apiErrorStatus,
  apiErrorText,
  blobErrorText,
  useGetAllFieldDailyReportsQuery,
  useGetFieldSitesQuery,
  useGetPtoProjectQuery,
  useGetPtoAosrRequisitesQuery,
  useListOzhrEntriesQuery,
  useSetOzhrRequisitesMutation,
  useLazyExportOzhrDocxQuery,
  type OzhrRequisites,
} from '@shared/api';
import {
  isoDateRu,
  ozhrMissingWeather,
  reportsMissingWeather,
  type FieldSite,
} from '@entities/field';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';
import { deletionDaysLeft } from './deletion';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  flex-wrap: wrap;
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 380px) 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Filters = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.spacing(3)};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const TableWrap = styled.div`
  overflow: auto;
  max-height: 68vh;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
`;

const Table = styled.table`
  border-collapse: collapse;
  width: 100%;
  font-size: 12px;

  th,
  td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
    padding: 6px 8px;
    text-align: left;
    vertical-align: top;
  }
  th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.colors.backgroundElevated};
    font-weight: 600;
    white-space: nowrap;
  }
  td.num {
    text-align: right;
    white-space: nowrap;
  }
`;

const Banner = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.text.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  font-size: 14px;
`;

const REQ_FIELDS: { key: keyof OzhrRequisites; label: string; placeholder?: string }[] = [
  { key: 'object_name', label: 'Объект капитального строительства' },
  { key: 'object_address', label: 'Адрес объекта' },
  { key: 'developer', label: 'Застройщик / технический заказчик' },
  { key: 'builder', label: 'Лицо, осуществляющее строительство' },
  { key: 'designer', label: 'Лицо, осуществляющее подготовку ПД' },
  { key: 'permit', label: 'Разрешение на строительство' },
];

function ozhrBlock(err: unknown): 'disabled' | 'project_deleted' | null {
  if (!err) return null;
  const text = apiErrorText(err, '');
  if (/помечен на удаление|в корзин/i.test(text)) return 'project_deleted';
  if (apiErrorStatus(err) === 404 && /ОЖР выключен/i.test(text)) return 'disabled';
  return null;
}

function fmtIsoDate(iso: string): string {
  return isoDateRu(iso) ?? iso;
}

const MISSING_DATES_SHOWN = 10;

const MissingList = styled.ul`
  margin: ${({ theme }) => theme.spacing(1.5)} 0 0;
  padding-left: ${({ theme }) => theme.spacing(5)};
`;

function SiteMissingWeather({ site, dates }: { site: FieldSite; dates: string[] }) {
  const { data } = useGetAllFieldDailyReportsQuery(site.id);
  const reports = reportsMissingWeather(data?.reports ?? [], dates);
  if (reports.length === 0) return null;
  return (
    <li>
      {site.name}:{' '}
      {reports.map((r, i) => (
        <Fragment key={r.id}>
          {i > 0 && ', '}
          <Link to={`/agents/field/${site.id}/reports/${r.id}`}>
            рапорт {isoDateRu(r.shift_date)} ({r.shift_type === 'night' ? 'ночь' : 'день'})
          </Link>
        </Fragment>
      ))}
    </li>
  );
}

function periodLabel(from: string, to: string): string {
  if (from && to) return `с ${fmtIsoDate(from)} по ${fmtIsoDate(to)} (включительно)`;
  if (from) return `с ${fmtIsoDate(from)} (включительно) и позже`;
  if (to) return `по ${fmtIsoDate(to)} (включительно)`;
  return 'за весь период';
}

export function PtoOzhr() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');
  const { data: projectData } = useGetPtoProjectQuery(projectId, { skip: !projectId });
  const { data: unifiedReqs } = useGetPtoAosrRequisitesQuery(projectId, { skip: !projectId });

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const inverted = !!from && !!to && from > to;
  const {
    currentData: payload,
    error,
    isFetching,
    refetch,
  } = useListOzhrEntriesQuery({ projectId, from, to }, { skip: !projectId || inverted });

  const [saveReq, { isLoading: saving }] = useSetOzhrRequisitesMutation();
  const [exportDocx, { isFetching: exporting }] = useLazyExportOzhrDocxQuery();

  const [req, setReq] = useState<OzhrRequisites>({});
  const [notice, setNotice] = useState<string | null>(null);

  // Префилл формы реквизитов из metadata.ozhr + единого паспорта объекта (АОСР, Договор Статут, РД).
  useEffect(() => {
    const meta = projectData?.project?.metadata as { ozhr?: OzhrRequisites } | undefined;
    const ozhr = meta?.ozhr ?? {};
    const r = unifiedReqs?.requisites;
    const fb = unifiedReqs?.fallback;
    setReq({
      object_name: ozhr.object_name || r?.object_name || fb?.object_name || '',
      object_address: ozhr.object_address || r?.object_address || fb?.object_address || '',
      developer: ozhr.developer || r?.developer || fb?.developer || '',
      builder: ozhr.builder || r?.builder || fb?.builder || '',
      designer: ozhr.designer || r?.designer || fb?.designer || '',
      permit: ozhr.permit || '',
      form_ref: ozhr.form_ref || r?.form_ref || '',
    });
  }, [projectData, unifiedReqs]);

  function onAutofillFromPassport() {
    const r = unifiedReqs?.requisites;
    const fb = unifiedReqs?.fallback;
    setReq((prev) => ({
      ...prev,
      object_name: prev.object_name?.trim() || r?.object_name || fb?.object_name || prev.object_name,
      object_address:
        prev.object_address?.trim() || r?.object_address || fb?.object_address || prev.object_address,
      developer: prev.developer?.trim() || r?.developer || fb?.developer || prev.developer,
      builder: prev.builder?.trim() || r?.builder || fb?.builder || prev.builder,
      designer: prev.designer?.trim() || r?.designer || fb?.designer || prev.designer,
    }));
    setNotice(
      'Реквизиты заполнены из единого паспорта объекта, Договора и штампов РД. Нажмите «Сохранить реквизиты», чтобы закрепить.',
    );
  }

  const block = ozhrBlock(error);
  const disabled = block === 'disabled';
  const projectDeletedAt = projectData?.project?.deleted_at ?? null;
  const projectDeleted = !!projectDeletedAt || block === 'project_deleted';
  const blocked = disabled || projectDeleted;

  async function onSaveRequisites() {
    setNotice(null);
    try {
      const clean: OzhrRequisites = {};
      for (const { key } of REQ_FIELDS) {
        const v = (req[key] || '').trim();
        if (v) clean[key] = v;
      }
      if (req.form_ref?.trim()) clean.form_ref = req.form_ref.trim();
      await saveReq({ projectId, requisites: clean }).unwrap();
      setNotice('Реквизиты сохранены в единый паспорт объекта (для ОЖР и АОСР).');
    } catch (e) {
      setNotice(apiErrorText(e, 'Не удалось сохранить реквизиты.'));
    }
  }

  async function onDownload() {
    setNotice(null);
    try {
      const blob = await exportDocx({ projectId, from, to }).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ozhr_${projectId}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setNotice(await blobErrorText(e, 'Не удалось сформировать ОЖР.'));
    }
  }

  // Строки — только ответа на ТЕКУЩИЙ период и без ошибки.
  const loaded = !!payload && !error;
  const rows = loaded ? (payload?.rows ?? []) : [];
  const section5 = loaded ? (payload?.section5 ?? []) : [];
  // Графа 3 «Условия» — погода смены из рапорта; не указана — графа пустая. Где
  // её указать — рапорты смены за эти даты на площадках проекта (КR8).
  const missing = ozhrMissingWeather(rows);
  const fieldRead = useAgentPermission('field').can_read;
  const { data: sitesData } = useGetFieldSitesQuery(
    { projectId },
    { skip: !projectId || !fieldRead || missing.count === 0 },
  );
  const sites = sitesData?.sites ?? [];

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Общий журнал работ</h1>
          <Muted>
            Черновик по форме № 1026/пр — раздел 3 из принятых фактов объёма. Выпуск за инженером.
          </Muted>
        </div>
        <Button
          onClick={onDownload}
          disabled={blocked || inverted || exporting || !projectId}
          title={inverted ? 'Дата «с» позже даты «по» — исправьте период' : undefined}
        >
          {exporting ? 'Формирую…' : 'Сформировать ОЖР (DOCX)'}
        </Button>
      </Row>

      {projectDeleted ? (
        <Banner>
          Проект помечен на удаление (в корзине
          {projectDeletedAt
            ? `, до безвозвратной зачистки — ${deletionDaysLeft(projectDeletedAt)} дн.`
            : ''}
          ) — ОЖР по нему не формируется. Восстановите проект в{' '}
          <Link to="/agents/pto">списке проектов ОСНОВА.Инженер</Link>, и журнал снова будет
          доступен.
        </Banner>
      ) : (
        disabled && (
          <Banner>
            Функция ОЖР отключена на сервере. Включите <code>OSNOVA_PTO_OZHR_ENABLED=1</code> в
            окружении backend, чтобы формировать журнал и вести реквизиты.
          </Banner>
        )
      )}
      {notice && <Banner>{notice}</Banner>}

      <Columns>
        {/* Реквизиты застройки (титульный лист + разделы). */}
        <Panel>
          <h3 style={{ marginTop: 0 }}>Единый паспорт реквизитов (ОЖР и АОСР)</h3>
          <Muted style={{ marginBottom: 12 }}>
            Идут в титульный лист ОЖР и шапку актов ИД (АОСР). Недостающие поля подтягиваются из
            Договора Статут и штампов РД.
          </Muted>
          {REQ_FIELDS.map(({ key, label }) => (
            <Field key={key}>
              {label}
              <Input
                value={req[key] ?? ''}
                onChange={(e) => setReq((p) => ({ ...p, [key]: e.target.value }))}
                disabled={!can_write || blocked}
              />
            </Field>
          ))}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button
              $variant="secondary"
              onClick={onSaveRequisites}
              disabled={!can_write || blocked || saving}
            >
              {saving ? 'Сохраняю…' : 'Сохранить реквизиты'}
            </Button>
            {can_write && !blocked && (
              <Button $variant="ghost" $size="sm" type="button" onClick={onAutofillFromPassport}>
                ⚡ Из Договора и РД
              </Button>
            )}
          </div>
        </Panel>

        {/* Витрина строк раздела 3 + фильтр периода. */}
        <div>
          <Filters>
            <Field style={{ marginBottom: 0 }}>
              Период с
              <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </Field>
            <Field style={{ marginBottom: 0 }}>
              по (включительно)
              <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </Field>
            <Button
              $variant="secondary"
              onClick={() => refetch()}
              disabled={blocked || inverted || isFetching}
            >
              Обновить
            </Button>
            <Muted style={{ marginLeft: 'auto' }}>
              {isFetching ? 'Загрузка…' : loaded ? `Строк раздела 3: ${rows.length}` : ''}
            </Muted>
          </Filters>
          <Muted style={{ marginBottom: 12 }}>
            Период: {periodLabel(from, to)}. Обе даты входят в выборку — и в DOCX тоже.
          </Muted>
          {inverted && (
            <Banner>
              Дата «с» позже даты «по» — за такой период записей быть не может. Исправьте период.
            </Banner>
          )}
          {error && !block && (
            <Banner>{apiErrorText(error, 'Не удалось загрузить строки журнала.')}</Banner>
          )}

          {loaded && rows.length === 0 && !isFetching && (
            <Muted>За выбранный период принятых фактов объёма нет.</Muted>
          )}
          {missing.count > 0 && (
            <Muted style={{ marginBottom: 12 }}>
              Графа «Условия» (погода смены) пуста в строках: {missing.count} из {rows.length}
              {missing.dates.length > 0 &&
                ` — за ${missing.dates.slice(0, MISSING_DATES_SHOWN).join(', ')}${
                  missing.dates.length > MISSING_DATES_SHOWN
                    ? ` и ещё ${missing.dates.length - MISSING_DATES_SHOWN}`
                    : ''
                }`}
              . Погоду берём из сменного рапорта. Указать её может проверяющий ПТО в карточке
              рапорта смены — в любом статусе рапорта (прораб — пока рапорт у него, в черновике или
              после возврата); графа обновится сама.
              {sites.length > 0 && (
                <MissingList>
                  {sites.map((s) => (
                    <SiteMissingWeather key={s.id} site={s} dates={missing.dates} />
                  ))}
                </MissingList>
              )}
            </Muted>
          )}

          {rows.length > 0 && (
            <TableWrap>
              <Table>
                <thead>
                  <tr>
                    <th>№</th>
                    <th>Дата</th>
                    <th>Условия</th>
                    <th>Наименование работ (с объёмом)</th>
                    <th>Представитель стройки</th>
                    <th>Папка</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i}>
                      <td className="num">{i + 1}</td>
                      <td>{r.date ?? ''}</td>
                      <td>{r.conditions ?? ''}</td>
                      <td>{r.work}</td>
                      <td>{r.representative ?? ''}</td>
                      <td>{r.folder ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableWrap>
          )}

          {/* Раздел 5 — перечень ИД из подписанных актов (только ответа на текущий запрос). */}
          {loaded && (
            <h3 style={{ marginBottom: 8 }}>Раздел 5. Перечень исполнительной документации</h3>
          )}
          {loaded && section5.length === 0 ? (
            <Muted>Подписанных актов исполнительной документации в проекте нет.</Muted>
          ) : (
            section5.length > 0 && (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>№</th>
                      <th>Наименование ИД</th>
                      <th>Дата подписания, комиссия</th>
                      <th>Папка</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section5.map((r, i) => (
                      <tr key={i}>
                        <td className="num">{i + 1}</td>
                        <td>{r.name}</td>
                        <td>{r.signed ?? ''}</td>
                        <td>{r.folder ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            )
          )}
        </div>
      </Columns>
    </div>
  );
}
